import {Show} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {useGetTelephonyCallQuery} from '@dms/api/settings';

import {useOnMount} from 'shared/web';

import {useCallPanel} from '../../hooks/useCallPanel';
import {useCurrentTelephonyCall} from '../../hooks/useCurrentTelephonyCall';
import {usePairableCall} from '../../hooks/usePairableCall';
import {isTelephonyCallNotFound, isUnkeptTelephonyCall} from '../../utils/isUnkeptTelephonyCall';
import {ForgetPairableCall} from './ForgetPairableCall';

/** How often a call not reported ended yet is re-read. */
const CALL_END_RECHECK_MS = 3000;

/**
 * Remembers the call the viewer is on (outbound, or inbound once answered) for pairing, and once
 * it ended opens the call panel in its after-call state until the viewer clicks Done or the next
 * call starts. Mounted next to the top-bar indicator, so it survives navigation.
 */

export function TelephonyCallAfterCall() {
  const {data: currentUser} = useGetCurrentUserInfoQuery();
  const {call, isOnCall} = useCurrentTelephonyCall();
  const {state} = usePairableCall();

  const viewerCallId = isOnCall && isNotNil(call) ? call.id : null;
  const userId = currentUser?.id;
  const endedCallState =
    isNotNil(state) &&
    !state.isDismissed &&
    state.userId === userId &&
    state.callId !== viewerCallId
      ? state
      : null;

  return (
    <>
      <Show when={isNotNil(viewerCallId) && isNotNil(userId) ? {viewerCallId, userId} : null}>
        {(remembered) => (
          <RememberPairableCall
            key={remembered.viewerCallId}
            callId={remembered.viewerCallId}
            userId={remembered.userId}
          />
        )}
      </Show>
      {/* Another user's call left in this tab: drop it with the customer data it holds. */}
      <Show
        when={isNotNil(state) && isNotNil(userId) && state.userId !== userId ? state.userId : null}
      >
        {(foreignUserId) => <ClearForeignCall key={foreignUserId} />}
      </Show>
      <Show when={endedCallState}>
        {(pairable) => <EndedCallPanelOpener key={pairable.callId} callId={pairable.callId} />}
      </Show>
    </>
  );
}

interface RememberPairableCallProps {
  callId: string;
  userId: string;
}

function RememberPairableCall(props: RememberPairableCallProps) {
  const {store} = usePairableCall();

  useOnMount(() => store.rememberCall(props.userId, props.callId));

  return null;
}

function ClearForeignCall() {
  const {store} = usePairableCall();

  useOnMount(() => store.clear());

  return null;
}

interface EndedCallPanelOpenerProps {
  callId: string;
}

/**
 * Opens the call panel on the ended call for pairing and the note, once the backend reports it
 * ended (the push may come late). A call the backend did not keep has nothing to pair: forgotten.
 */
function EndedCallPanelOpener(props: EndedCallPanelOpenerProps) {
  const [isSettled, setIsSettled] = useState(false);
  const {data: call, error} = useGetTelephonyCallQuery(
    {callId: props.callId},
    {pollingInterval: isSettled ? 0 : CALL_END_RECHECK_MS, skipPollingIfUnfocused: true}
  );

  const isGone = isTelephonyCallNotFound(error) || (isNotNil(call) && isUnkeptTelephonyCall(call));
  const isEnded = call?.status === 'ended';

  if ((isGone || isEnded) !== isSettled) {
    setIsSettled(isGone || isEnded);
  }

  if (isGone) {
    return <ForgetPairableCall callId={props.callId} />;
  }

  return (
    <Show when={isEnded}>
      <OpenCallPanel />
    </Show>
  );
}

function OpenCallPanel() {
  const {store} = useCallPanel();

  useOnMount(() => store.open());

  return null;
}
